import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getBuyerOrderBreakdown,
  bundlePostageLabelForBreakdown,
  getLineOriginalUnitPrice,
  getLinePaidUnitPrice,
} from "../../utils/orderPriceBreakdown";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { AiOutlineArrowLeft, AiOutlineClockCircle, AiFillStar, AiOutlineStar } from "react-icons/ai";
import { IoReceiptOutline } from "react-icons/io5";
import { BsFillBagFill } from "react-icons/bs";
import { BiMessageSquareDetail } from "react-icons/bi";
import { MdOutlineLocalShipping, MdOutlinePayment } from "react-icons/md";
import { RxCross1 } from "react-icons/rx";
import { backend_url, server } from "../../server";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import Ratings from "../Products/Ratings";
import { useMarkNotificationsReadOnPage } from "../../hooks/useMarkNotificationsReadOnPage";
import {
  getOrderEstimatedArrivalDisplay,
  getEstimatedDeliveryDaysNumber,
  getOrderPlacedAtFormatted,
  getOrderSellerDispatchTimeDays,
  getSellerDispatchDeadlineFormatted,
  isSellerDispatchOverdue,
  isOrderCancelled,
} from "../../utils/orderEta";
import { isOrderCompleted, isVaMediationInProgress } from "../../utils/orderListStatus";
import MobileFriendlyModal, { MODAL_TEXTAREA_CLASS } from "../Layout/MobileFriendlyModal";
import { useScrollToRefOnMobile } from "../../utils/navigateWithMobileDashboardScroll";
import { orderRequiresUkara } from "../../utils/ukaraRequirement";

const primaryButtonClasses =
  "inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:cursor-not-allowed disabled:bg-slate-400";
const secondaryButtonClasses =
  "inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400";

/** Normalize UKARA status from API or local state (case / whitespace safe). */
function normalizeUkaraDecision(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "verified" || s === "rejected") return s;
  if (s === "not_required") return "not_required";
  return "pending";
}

const OrderDetails = () => {
  const { shopOrders, isLoading } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();
  const pageTopRef = useRef(null);
  useScrollToRefOnMobile(pageTopRef, id);
  useMarkNotificationsReadOnPage("seller_order_detail");
  const [ukaraStatus, setUkaraStatus] = useState("");
  const [isUpdatingUkara, setIsUpdatingUkara] = useState(false);
  const [estimatedDeliveryDays, setEstimatedDeliveryDays] = useState("");
  const [estimatedDeliveryConfirmed, setEstimatedDeliveryConfirmed] = useState(false);
  const [trackingCode, setTrackingCode] = useState("");
  const [trackingCourier, setTrackingCourier] = useState("");
  const [trackingLink, setTrackingLink] = useState("");
  const [customerRating, setCustomerRating] = useState(0);
  const [customerRatingComment, setCustomerRatingComment] = useState("");
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [existingCustomerReview, setExistingCustomerReview] = useState(null);
  const [customerAverageRating, setCustomerAverageRating] = useState(0);
  const [platformFees, setPlatformFees] = useState({ enabled: true, percentage: 5 });
  const [singleOrder, setSingleOrder] = useState(null);
  const [singleOrderLoading, setSingleOrderLoading] = useState(false);
  const [reviewExchange, setReviewExchange] = useState(null);
  const [vaInboxLink, setVaInboxLink] = useState(null);
  const [vaAppealNote, setVaAppealNote] = useState("");
  const [vaAppealing, setVaAppealing] = useState(false);

  const sellerId = user?._id;

  useEffect(() => {
    if (sellerId && user?.isSeller) {
      dispatch(getAllOrdersOfShop(sellerId));
    }
  }, [dispatch, sellerId, user?.isSeller]);

  // Fetch single order when not in list (e.g. direct link from notification)
  useEffect(() => {
    const fetchSingleOrder = async () => {
      if (!id || !sellerId || !user?.isSeller) return;
      setSingleOrderLoading(true);
      try {
        const { data } = await axios.get(`${server}/order/seller-order/${id}`, {
          withCredentials: true,
        });
        if (data.success && data.order) {
          setSingleOrder(data.order);
        }
      } catch (err) {
        setSingleOrder(null);
      } finally {
        setSingleOrderLoading(false);
      }
    };
    if (id && sellerId && user?.isSeller && !isLoading && !shopOrders?.find((o) => o._id === id)) {
      fetchSingleOrder();
    } else if (shopOrders?.find((o) => o._id === id)) {
      setSingleOrder(null);
    }
  }, [id, sellerId, user?.isSeller, isLoading, shopOrders]);

  // Fetch platform fees settings
  useEffect(() => {
    const fetchPlatformFees = async () => {
      try {
        const { data } = await axios.get(`${server}/options/payment-settings`, {
          withCredentials: true,
        });
        if (data?.settings?.platformFees) {
          setPlatformFees(data.settings.platformFees);
        }
      } catch (error) {
        // Failed to fetch platform fees settings
      }
    };
    fetchPlatformFees();
  }, []);

  const orderFromList = useMemo(
    () => shopOrders?.find((item) => item._id === id),
    [shopOrders, id]
  );
  const order = orderFromList || singleOrder;

  const fetchReviewExchange = async () => {
    if (!order?._id || !isOrderCompleted(order)) {
      setReviewExchange(null);
      return;
    }
    try {
      const { data } = await axios.get(`${server}/order/review-exchange/${order._id}`, {
        withCredentials: true,
      });
      if (data.success) setReviewExchange(data.exchange);
    } catch {
      setReviewExchange(null);
    }
  };

  useEffect(() => {
    fetchReviewExchange();
  }, [order?._id, order?.userReceivedConfirmed, order?.payoutReleased]);

  useEffect(() => {
    if (!order?._id || !isVaMediationInProgress(order)) {
      setVaInboxLink(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${server}/order/va-mediation-inbox/${order._id}`, {
          withCredentials: true,
        });
        if (!cancelled && data?.canMessage && data?.inboxLink) {
          setVaInboxLink(data.inboxLink);
        } else if (!cancelled) {
          setVaInboxLink(null);
        }
      } catch {
        if (!cancelled) setVaInboxLink(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?._id, order?.vaMediation?.status]);

  const canAppealVaDecision = useMemo(() => {
    const vm = order?.vaMediation;
    if (!vm || vm.status !== "resolved" || vm.appealedAt) return false;
    if (!vm.appealDeadline) return true;
    return new Date(vm.appealDeadline) > new Date();
  }, [order?.vaMediation]);

  const handleVaAppeal = async () => {
    if (!order?._id || !sellerId || !vaAppealNote.trim()) {
      toast.error("Please explain why you are appealing this decision.");
      return;
    }
    setVaAppealing(true);
    try {
      await axios.post(
        `${server}/order/va-appeal/${order._id}`,
        { appealNote: vaAppealNote.trim() },
        { withCredentials: true }
      );
      toast.success("Your appeal has been submitted. Veteran Airsoft will review again.");
      setVaAppealNote("");
      dispatch(getAllOrdersOfShop(sellerId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not submit your appeal.");
    } finally {
      setVaAppealing(false);
    }
  };

  const clearReviewQueryParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("review") !== "1") return;
    params.delete("review");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!order || !isOrderCompleted(order)) return;
    const params = new URLSearchParams(location.search);
    if (params.get("review") !== "1") return;
    const section = document.getElementById("order-reviews");
    if (section) {
      setTimeout(() => section.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
    if (order.customerReviewSubmitted || existingCustomerReview) return;
    if (reviewExchange && !reviewExchange.youSubmitted) {
      setIsRatingModalOpen(true);
    }
  }, [order, location.search, reviewExchange, existingCustomerReview]);

  useEffect(() => {
    if (!order) return;
    const needsUkara = orderRequiresUkara(order);
    if (needsUkara) {
      const d = normalizeUkaraDecision(order.ukaraStatus);
      setUkaraStatus(d === "not_required" ? "pending" : d);
    } else {
      setUkaraStatus("not_required");
    }
    if (order?.estimatedDeliveryDays) {
      setEstimatedDeliveryDays(order.estimatedDeliveryDays);
    }
    setEstimatedDeliveryConfirmed(
      Boolean(
        order?.sellerEstimatedDeliverySavedAt ||
          order?.estimatedDeliverySetAt ||
          order?.dispatchedAt
      )
    );
    setTrackingCode(order?.trackingCode ? String(order.trackingCode) : "");
    setTrackingCourier(order?.trackingCourier ? String(order.trackingCourier) : "");
    setTrackingLink(order?.trackingLink ? String(order.trackingLink) : "");
  }, [
    order?._id,
    order?.cart,
    order?.ukaraStatus,
    order?.estimatedDeliveryDays,
    order?.sellerEstimatedDeliverySavedAt,
    order?.estimatedDeliverySetAt,
    order?.dispatchedAt,
    order?.trackingCode,
    order?.trackingCourier,
    order?.trackingLink,
  ]);

  // Fetch existing customer review for this order
  useEffect(() => {
    const fetchCustomerReview = async () => {
      if (!order?._id) return;
      try {
        const { data } = await axios.get(`${server}/order/customer-review/${order._id}`, {
          withCredentials: true,
        });
        if (data.success && data.review) {
          setExistingCustomerReview(data.review);
          setCustomerRating(data.review.rating);
          setCustomerRatingComment(data.review.comment || "");
        }
        if (data.averageRating) {
          setCustomerAverageRating(data.averageRating);
        }
      } catch (error) {
        // Review doesn't exist yet, that's fine
        // No existing review found
      }
    };
    fetchCustomerReview();
  }, [order?._id]);

  const orderItems = order?.cart || [];
  const totalQuantity = orderItems.reduce((acc, item) => acc + item.qty, 0);
  const priceBreakdown = useMemo(() => getBuyerOrderBreakdown(order), [order]);
  const estimatedArrivalFormatted = useMemo(
    () => getOrderEstimatedArrivalDisplay(order),
    [order?.estimatedDeliveryDays, order?.dispatchedAt]
  );
  const sellerOverviewHasDeliveryDays = order ? getEstimatedDeliveryDaysNumber(order) != null : false;
  const canJumpToTracking =
    order?.status !== "Delivered" && !isOrderCancelled(order);

  const orderRequiresUkaraReview = useMemo(
    () => orderRequiresUkara(order),
    [order]
  );

  const effectiveUkaraDecision = useMemo(() => {
    if (!orderRequiresUkaraReview) return "not_required";
    const fromOrder = normalizeUkaraDecision(order?.ukaraStatus);
    if (fromOrder === "verified" || fromOrder === "rejected") return fromOrder;
    const fromLocal = normalizeUkaraDecision(ukaraStatus);
    if (fromLocal === "verified" || fromLocal === "rejected") return fromLocal;
    return "pending";
  }, [orderRequiresUkaraReview, order?.ukaraStatus, ukaraStatus]);

  const ukaraGateBlocksFulfillment = useMemo(
    () => orderRequiresUkaraReview && effectiveUkaraDecision === "pending",
    [orderRequiresUkaraReview, effectiveUkaraDecision]
  );

  const sellerDispatchTimeDays = useMemo(
    () => (order ? getOrderSellerDispatchTimeDays(order) : 1),
    [order]
  );

  const dispatchOverdue = useMemo(
    () =>
      Boolean(
        order &&
          !ukaraGateBlocksFulfillment &&
          isSellerDispatchOverdue(order)
      ),
    [order, ukaraGateBlocksFulfillment]
  );

  /** True while local tracking fields match the server — disables repeat save until something changes. */
  const trackingUnchangedFromSaved = useMemo(() => {
    if (!order) return true;
    const savedCode = String(order.trackingCode ?? "").trim();
    const savedCourier = String(order.trackingCourier ?? "").trim();
    const savedLink = String(order.trackingLink ?? "").trim();
    const localCode = String(trackingCode ?? "").trim();
    const localCourier = String(trackingCourier ?? "").trim();
    const localLink = String(trackingLink ?? "").trim();
    return (
      savedCode === localCode && savedCourier === localCourier && savedLink === localLink
    );
  }, [
    order?.trackingCode,
    order?.trackingCourier,
    order?.trackingLink,
    trackingCode,
    trackingCourier,
    trackingLink,
  ]);

  const estimatedDeliveryUnchangedFromSaved = useMemo(() => {
    const stepSaved = Boolean(
      estimatedDeliveryConfirmed ||
        order?.sellerEstimatedDeliverySavedAt ||
        order?.estimatedDeliverySetAt ||
        order?.dispatchedAt
    );
    if (!stepSaved || !order) return false;
    const savedDays = Number(order.estimatedDeliveryDays);
    const localDays = Number(estimatedDeliveryDays);
    if (!Number.isFinite(savedDays) || !Number.isFinite(localDays)) return false;
    return savedDays === localDays;
  }, [
    estimatedDeliveryConfirmed,
    order,
    order?.sellerEstimatedDeliverySavedAt,
    order?.estimatedDeliverySetAt,
    order?.dispatchedAt,
    order?.estimatedDeliveryDays,
    estimatedDeliveryDays,
  ]);

  const ukaraVerifyButtonsDisabled = useMemo(
    () => isUpdatingUkara || effectiveUkaraDecision !== "pending",
    [isUpdatingUkara, effectiveUkaraDecision]
  );

  const handleJumpToTracking = useMemo(
    () => () => {
      if (!order || order.status === "Delivered" || isOrderCancelled(order)) {
        toast.info("Tracking section is not available for this order status.");
        return;
      }
      const needs = orderRequiresUkara(order);
      const ukaraDone =
        !needs ||
        normalizeUkaraDecision(order.ukaraStatus) === "verified" ||
        normalizeUkaraDecision(order.ukaraStatus) === "rejected" ||
        normalizeUkaraDecision(ukaraStatus) === "verified" ||
        normalizeUkaraDecision(ukaraStatus) === "rejected";
      const targetId = ukaraDone ? "seller-shipment-tracking" : "shipment-tracking";
      const section = document.getElementById(targetId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        toast.info("Shipping section is not available.");
      }
    },
    [order, ukaraStatus]
  );

  useEffect(() => {
    if (!order) return;
    if (location.hash !== "#shipment-tracking") return;
    const section = document.getElementById("shipment-tracking");
    if (section) {
      setTimeout(() => {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  }, [location.hash, order]);

  // Open or create conversation with buyer (reuses existing chat – no duplicate)
  const openChatWithBuyer = async () => {
    const buyerId = order?.user?._id ?? order?.user;
    if (!buyerId || !sellerId) {
      toast.error("Customer information not available");
      return;
    }
    try {
      const groupTitle = `${sellerId}_${buyerId}`;
      const res = await axios.post(
        `${server}/conversation/create-new-conversation`,
        { groupTitle, userId: buyerId, sellerId },
        { withCredentials: true }
      );
      const conversationId =
        res.data?.existingConversation?._id ||
        res.data?.conversation?._id ||
        res.data?._id;
      if (conversationId) {
        navigate(`/profile/inbox?conversation=${conversationId}`);
      } else {
        toast.error("Unable to open conversation");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to open chat.");
    }
  };

  const updateUkaraStatus = async (newStatus) => {
    if (!order) return;
    if (isOrderCancelled(order)) {
      toast.error("This order is cancelled. UKARA cannot be updated.");
      return;
    }
    if (!orderRequiresUkaraReview) {
      toast.info("This order does not require UKARA verification.");
      return;
    }
    if (effectiveUkaraDecision !== "pending") {
      toast.info("UKARA has already been reviewed for this order.");
      return;
    }
    if (effectiveUkaraDecision === newStatus) {
      toast.info("UKARA status is already set to this value.");
      return;
    }

    try {
      setIsUpdatingUkara(true);
      await axios.put(
        `${server}/order/update-ukara-status/${order._id}`,
        { status: newStatus },
        {
          withCredentials: true,
        }
      );
      toast.success(
        newStatus === "verified"
          ? "UKARA number verified."
          : "UKARA number marked as rejected."
      );
      setUkaraStatus(newStatus);
      dispatch(getAllOrdersOfShop(sellerId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update UKARA status");
    } finally {
      setIsUpdatingUkara(false);
    }
  };

  const handleEstimatedDeliverySave = async () => {
    if (!order) return;
    if (isOrderCancelled(order)) {
      toast.error("This order is cancelled. Estimated delivery cannot be updated.");
      return;
    }
    if (ukaraGateBlocksFulfillment) {
      toast.error("Verify or reject the buyer’s UKARA number before saving estimated delivery.");
      return;
    }
    if (!estimatedDeliveryDays || estimatedDeliveryDays < 1 || estimatedDeliveryDays > 30) {
      toast.error("Please select estimated delivery days between 1 and 30");
      return;
    }

    try {
      await axios.put(
        `${server}/order/update-estimated-delivery/${order._id}`,
        {
          estimatedDeliveryDays: Number(estimatedDeliveryDays),
        },
        {
          withCredentials: true,
        }
      );
      toast.success("Estimated delivery updated.");
      setEstimatedDeliveryConfirmed(true);
      dispatch(getAllOrdersOfShop(sellerId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update estimated delivery");
    }
  };

  const handleTrackingSave = async () => {
    if (!order) return;
    if (isOrderCancelled(order)) {
      toast.error("This order is cancelled. Tracking cannot be updated.");
      return;
    }
    if (ukaraGateBlocksFulfillment) {
      toast.error("Verify or reject the buyer’s UKARA number before adding tracking or marking dispatched.");
      return;
    }
    const code = String(trackingCode || "").trim();
    const link = String(trackingLink || "").trim();
    if (!code && !link) {
      toast.error("Enter a tracking code or a tracking link before marking this order as dispatched.");
      return;
    }

    try {
      await axios.put(
        `${server}/order/update-tracking/${order._id}`,
        {
          trackingCode: code,
          trackingCourier: String(trackingCourier || "").trim(),
          trackingLink: link,
          trackingStatus: "active",
        },
        {
          withCredentials: true,
        }
      );
      toast.success("Tracking details updated.");
      dispatch(getAllOrdersOfShop(sellerId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update tracking details");
    }
  };

  const handleCustomerRatingSubmit = async () => {
    if (!order || !customerRating || customerRating < 1 || customerRating > 5) {
      toast.error("Please select a rating between 1 and 5 stars");
      return;
    }

    try {
      setIsSubmittingRating(true);
      const { data } = await axios.post(
        `${server}/order/create-customer-review/${order._id}`,
        {
          rating: customerRating,
          comment: customerRatingComment,
        },
        {
          withCredentials: true,
        }
      );

      if (data.success) {
        toast.success("Customer review submitted successfully");
        setExistingCustomerReview(data.review);
        setCustomerAverageRating(data.averageRating);
        setIsRatingModalOpen(false);
        clearReviewQueryParam();
        setReviewExchange((prev) => (prev ? { ...prev, youSubmitted: true } : prev));
        fetchReviewExchange();
        if (sellerId) dispatch(getAllOrdersOfShop(sellerId));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to submit customer review");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  return (
    <div ref={pageTopRef} className="min-h-[70vh]  py-6 sm:py-10">
      <div className="mx-auto max-w-5xl px-4">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#38513b]/10 text-[#38513b]">
              <BsFillBagFill size={20} />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                Order details
              </h1>
              <p className="text-sm text-slate-500">
                Review fulfillment information and update order status.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/profile/orders?view=selling" className={secondaryButtonClasses}>
              <AiOutlineArrowLeft size={18} />
              Order list
            </Link>
            
          </div>
        </header>

        {(isLoading || singleOrderLoading) && !order && (
          <div className="mt-10 space-y-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="h-24 animate-pulse rounded-2xl bg-slate-200" />
            ))}
          </div>
        )}

        {!isLoading && !singleOrderLoading && !order && (
          <div className="mt-10 rounded-3xl border border-dashed border-slate-200 bg-white/60 p-12 text-center shadow-sm">
            <IoReceiptOutline className="mx-auto h-12 w-12 text-slate-300" />
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              Order not found
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              We couldn’t locate this order in your seller orders. Please verify the link
              or refresh the page.
            </p>
          </div>
        )}

        {order && (
          <div className="mt-8 space-y-6">
            {/* Quick actions for refund + tracking */}
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Quick actions</h2>
                  <p className="text-sm text-slate-500">
                    Jump to refund review or the shipping steps (UKARA, delivery estimate, tracking).
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {((order.status || "").toLowerCase().includes("refund") ||
                    (order.refunds && order.refunds.length > 0) ||
                    order.refundReason) && (
                    <Link
                      to={`/profile/disputes-refunds?tab=review-refunds&order=${order._id}`}
                      className="inline-flex h-10 items-center justify-center rounded-xl bg-amber-600 px-4 text-sm font-semibold text-white transition hover:bg-amber-700"
                    >
                      Open refund review
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={handleJumpToTracking}
                    disabled={!canJumpToTracking}
                    className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-[#38513b] hover:text-[#38513b] disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      canJumpToTracking
                        ? "Jump to UKARA (if required), delivery estimate, and tracking"
                        : isOrderCancelled(order)
                        ? "Shipping steps are unavailable for cancelled orders"
                        : "Shipping steps are unavailable after delivery"
                    }
                  >
                    Go to shipping steps
                  </button>
                </div>
              </div>
            </section>

            {/* Refund request banner – show when customer requested refund */}
            {(order.status === "Processing refund" ||
              (order.refunds && order.refunds.some((r) => r.status === "Processing refund"))) && (
              <section className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-amber-900 mb-2">Refund requested</h2>
                <p className="text-sm text-amber-800 mb-4">
                  The customer has requested a refund for this order.
                  {order.refunds?.filter((r) => r.status === "Processing refund").length > 0 && (
                    <span>
                      {" "}
                      {order.refunds.filter((r) => r.status === "Processing refund").length} item(s) awaiting
                      your review.
                    </span>
                  )}
                </p>
                <Link
                  to={`/profile/disputes-refunds?tab=review-refunds&order=${order._id}`}
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-amber-600 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700"
                >
                  View details & review refund
                </Link>
              </section>
            )}

            {isVaMediationInProgress(order) && (
              <section className="rounded-3xl border-2 border-[#38513b]/40 bg-[#f6faf7] p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">
                  {order.vaMediation?.status === "mediating"
                    ? "VA mediation in progress"
                    : "Veteran Airsoft is reviewing this dispute"}
                </h2>
                <p className="text-sm text-slate-700">
                  {order.vaMediation?.status === "mediating"
                    ? "Our team may contact you privately for evidence or clarification. Respond in your VA chat — the buyer cannot see your thread."
                    : "The buyer asked VA to step in. Our team may contact you or issue a decision — you will be notified here and in your notifications."}
                </p>
                {vaInboxLink && order.vaMediation?.status === "mediating" ? (
                  <Link
                    to={vaInboxLink}
                    className={`${primaryButtonClasses} mt-4`}
                  >
                    Open VA mediation chat
                  </Link>
                ) : null}
              </section>
            )}

            {order.vaMediation?.status === "resolved" && order.vaMediation?.outcomeSummary && (
              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">VA decision</h2>
                <p className="text-xs font-semibold uppercase text-[#38513b] mb-2">
                  {order.vaMediation.outcomeType === "full_refund" && "Full refund"}
                  {order.vaMediation.outcomeType === "partial_refund" && "Partial refund"}
                  {order.vaMediation.outcomeType === "mediation" && "Mediation"}
                  {order.vaMediation.outcomeType === "case_closed" && "Case closed"}
                  {order.vaMediation.outcomeType === "return_required" && "Return required"}
                </p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.vaMediation.outcomeSummary}</p>
                {canAppealVaDecision && (
                  <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
                    <p className="text-xs text-slate-600">
                      You can appeal this decision within 7 days if you disagree.
                    </p>
                    <textarea
                      value={vaAppealNote}
                      onChange={(e) => setVaAppealNote(e.target.value)}
                      rows={3}
                      placeholder="Explain why you are appealing…"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    />
                    <button
                      type="button"
                      onClick={handleVaAppeal}
                      disabled={vaAppealing}
                      className={`${secondaryButtonClasses} w-full justify-center`}
                    >
                      {vaAppealing ? "Submitting…" : "Appeal this decision"}
                    </button>
                  </div>
                )}
              </section>
            )}

            <section className="grid gap-6 lg:grid-cols-[minmax(0,2fr),minmax(0,1fr)]">
              <article className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      Order overview
                    </h2>
                    <p className="text-sm text-slate-500">
                      Order ID{" "}
                      <span className="font-semibold text-slate-900">
                        #{order?.orderNumber ?? order?._id?.toString().substring(0, 8) ?? "N/A"}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-right text-sm text-slate-500">
                    {isOrderCancelled(order) ? (
                      <span className="inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                        Cancelled
                      </span>
                    ) : null}
                    <div className="flex items-center gap-1">
                      <AiOutlineClockCircle size={16} />
                      Placed on{" "}
                      <span className="font-semibold text-slate-900">
                        {getOrderPlacedAtFormatted(order) || "—"}
                      </span>
                    </div>
                    {!isOrderCancelled(order) ? (
                    <div className="flex flex-wrap items-center justify-end gap-1 text-right">
                      <MdOutlineLocalShipping size={16} className="shrink-0 text-slate-400" aria-hidden />
                      <span>
                        Estimated arrival{" "}
                        <span className="font-semibold text-slate-900">
                          {estimatedArrivalFormatted || "Not available"}
                        </span>
                      </span>
                    </div>
                    ) : null}
                    {!isOrderCancelled(order) && !estimatedArrivalFormatted && (
                      <p className="max-w-[18rem] text-right text-xs text-slate-500">
                        {sellerOverviewHasDeliveryDays
                          ? "Order date missing — ETA cannot be calculated."
                          : "Set estimated delivery days in shipment tracking below so buyers see an arrival window."}
                      </p>
                    )}
                    <div>
                      {totalQuantity}{" "}
                      {totalQuantity === 1 ? "item" : "items"} •{" "}
                      <span className="font-semibold text-[#38513b]">
                        {formatCurrency(priceBreakdown.totalCharged)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {orderItems.map((item) => (
                    <div
                      key={`${item._id}-${item.name}`}
                      className="flex flex-col gap-4 rounded-2xl border border-slate-100 /60 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
                          {item?.images?.[0] ? (
                            <img
                              src={`${backend_url}/${item.images[0]}`}
                              alt={item.name}
                              className="h-full w-full object-contain p-3"
                            />
                          ) : (
                            <span className="text-xs font-semibold text-slate-400">
                              No image
                            </span>
                          )}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            {item.name}
                          </h3>
                          <p className="text-sm text-slate-500">
                            Variant:{" "}
                            <span className="font-medium text-slate-700">
                              {item.variant || item._id?.slice(-6) || "Standard"}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm font-medium text-slate-700">
                        <span className="text-slate-500">
                          Qty{" "}
                          <span className="font-semibold text-slate-900">
                            {item.qty}
                          </span>
                        </span>
                        <span className="text-[#38513b]">
                          {(() => {
                            const paid = getLinePaidUnitPrice(item);
                            const was = getLineOriginalUnitPrice(item);
                            const showWas = was > paid + 0.001;
                            return (
                              <>
                                {showWas ? (
                                  <span className="mr-2 text-xs font-normal text-slate-400 line-through">
                                    {formatCurrency(was)}
                                  </span>
                                ) : null}
                                {formatCurrency(paid)}
                                {item?.isBundleItemFromOffer ? (
                                  <span className="ml-2 text-[10px] font-semibold uppercase text-emerald-700">
                                    Offer
                                  </span>
                                ) : null}
                              </>
                            );
                          })()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              <aside className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <header className="flex items-center gap-2 text-slate-900">
                    <MdOutlineLocalShipping size={20} />
                    <h2 className="text-base font-semibold">Shipping details</h2>
                  </header>
                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Customer</span>
                      <span className="font-medium text-slate-900">
                        {order?.user?.name || "—"}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                      <button
                        type="button"
                        onClick={openChatWithBuyer}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]"
                      >
                        <BiMessageSquareDetail size={18} />
                        Message buyer
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Customer Rating</span>
                      <div className="flex items-center gap-2">
                        {customerAverageRating > 0 ? (
                          <>
                            <Ratings rating={customerAverageRating} />
                            <span className="font-medium text-slate-900">
                              ({customerAverageRating.toFixed(1)}/5)
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-400">No ratings yet</span>
                        )}
                      </div>
                    </div>
                    {isOrderCompleted(order) && (
                      <div className="pt-2 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={() => setIsRatingModalOpen(true)}
                          className="w-full text-sm font-medium text-[#38513b] hover:text-[#2f4232] transition"
                        >
                          {existingCustomerReview ? "Update Customer Rating" : "Rate Customer"}
                        </button>
                      </div>
                    )}
                    <div>
                      <dt className="text-slate-500">Address</dt>
                      <dd className="mt-1 font-medium text-slate-900">
                        {order?.shippingAddress
                          ? `${order.shippingAddress.address1 || ""} ${
                              order.shippingAddress.address2 || ""
                            }`
                          : "—"}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>City</span>
                      <span className="font-medium text-slate-900">
                        {order?.shippingAddress?.city || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Country</span>
                      <span className="font-medium text-slate-900">
                        {order?.shippingAddress?.country || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Contact</span>
                      <span className="font-medium text-slate-900">
                        {order?.user?.phoneNumber ||
                          order?.shippingAddress?.phone ||
                          "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>UKARA number</span>
                      <span className="font-semibold text-slate-900">
                        {order?.ukaraNumber || order?.user?.ukaraNumber || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>UKARA proof</span>
                      {order?.user?.ukaraProofImage ? (
                        <a
                          href={`${backend_url}${order.user.ukaraProofImage}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-blue-600 hover:underline"
                        >
                          View proof
                        </a>
                      ) : (
                        <span className="font-medium text-slate-900">—</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Verification status</span>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          effectiveUkaraDecision === "verified"
                            ? "bg-emerald-100 text-emerald-700"
                            : effectiveUkaraDecision === "rejected"
                            ? "bg-rose-100 text-rose-700"
                            : effectiveUkaraDecision === "not_required"
                            ? "bg-slate-100 text-slate-600"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {effectiveUkaraDecision === "pending"
                          ? "Pending review"
                          : effectiveUkaraDecision === "verified"
                          ? "Verified"
                          : effectiveUkaraDecision === "not_required"
                          ? "Not required"
                          : "Rejected"}
                      </span>
                    </div>
                  </dl>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <header className="flex items-center gap-2 text-slate-900">
                    <MdOutlinePayment size={20} />
                    <h2 className="text-base font-semibold">Price Breakdown</h2>
                  </header>
                  <dl className="mt-4 space-y-3 text-sm">
                    {(() => {
                      const subtotal = priceBreakdown.itemsSubtotal;
                      const postageTotal = priceBreakdown.shipping;
                      const buyerProtectionFees = priceBreakdown.buyerProtectionFee;
                      const couponDiscount = priceBreakdown.couponDiscount;
                      const bundleDiscount = priceBreakdown.bundleDiscount;
                      const platformFeeAmount = platformFees.enabled
                        ? (subtotal * (platformFees.percentage || 0)) / 100
                        : 0;
                      const netMerchandiseAndShipping =
                        subtotal - couponDiscount - bundleDiscount + postageTotal;
                      const orderEarnings = netMerchandiseAndShipping - platformFeeAmount;
                      const orderTotal = priceBreakdown.totalCharged;

                      return (
                        <>
                          {/* Stage 1: What the customer pays */}
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>Items (bundle discounts)</span>
                            <span className="text-emerald-600 font-semibold">+ {formatCurrency(subtotal)}</span>
                          </div>
                          {couponDiscount > 0 ? (
                            <div className="flex items-center justify-between text-sm text-slate-500">
                              <span>
                                Coupon / code discount
                                {priceBreakdown.discountCode
                                  ? ` (${priceBreakdown.discountCode})`
                                  : ""}
                              </span>
                              <span className="text-rose-600 font-semibold">
                                - {formatCurrency(couponDiscount)}
                              </span>
                            </div>
                          ) : null}
                          {bundleDiscount > 0 ? (
                            <div className="flex items-center justify-between text-sm text-slate-500">
                              <span>Bundle deal discount</span>
                              <span className="text-rose-600 font-semibold">
                                - {formatCurrency(bundleDiscount)}
                              </span>
                            </div>
                          ) : null}
                          {priceBreakdown.bundlePostageDiscount > 0 ? (
                            <div className="flex items-center justify-between text-sm text-slate-500">
                              <span>{bundlePostageLabelForBreakdown(priceBreakdown)}</span>
                              <span className="text-rose-600 font-semibold">
                                - {formatCurrency(priceBreakdown.bundlePostageDiscount)}
                              </span>
                            </div>
                          ) : null}
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>Buyer protection fee</span>
                            <span className="text-emerald-600 font-semibold">+ {formatCurrency(buyerProtectionFees)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>Shipping</span>
                            <span className="text-emerald-600 font-semibold">+ {formatCurrency(postageTotal)}</span>
                          </div>
                          <div className="flex items-center justify-between pt-2 text-sm text-slate-500 border-t border-slate-200">
                            <span>Order total (charged)</span>
                            <span className="text-slate-700 font-semibold">= {formatCurrency(orderTotal)}</span>
                          </div>

                          <div className="border-t border-slate-200 my-3" />

                          {/* Stage 2: What seller actually earns */}
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>Buyer protection fee</span>
                            <span className="text-rose-600 font-semibold">- {formatCurrency(buyerProtectionFees)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>VA Fee ({platformFees.percentage || 0}% of items)</span>
                            <span className="text-rose-600 font-semibold">- {formatCurrency(platformFeeAmount)}</span>
                          </div>

                          <div className="flex items-center justify-between pt-3 border-t border-slate-200 text-sm">
                            <span>Order earnings (est.)</span>
                            <span className="text-slate-700 font-semibold">= {formatCurrency(orderEarnings)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </dl>
                </div>
              </aside>
            </section>

            {isOrderCancelled(order) ? (
              <section className="rounded-3xl border-2 border-slate-300 bg-slate-50 p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Order status
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">Cancelled</h2>
                <p className="mt-2 text-sm text-slate-600">
                  {order?.buyerDispatchDelayChoice === "cancel"
                    ? "The buyer cancelled this order for a full refund because it was not dispatched in time. Shipping steps are closed."
                    : "This order has been cancelled. Shipping steps are closed and it can no longer be marked as dispatched."}
                </p>
                <span className="mt-4 inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                  Cancelled
                </span>
              </section>
            ) : null}

            {/* UKARA (required first when applicable) → Estimated delivery → Tracking / dispatch */}
            {order?.status !== "Delivered" && !isOrderCancelled(order) && (
              <div id="shipment-tracking" className="space-y-6">
                {dispatchOverdue ? (
                  <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-950">
                    <p className="font-semibold">Dispatch window passed</p>
                    <p className="mt-1 text-amber-900">
                      This order should have been shipped within {sellerDispatchTimeDays} day
                      {sellerDispatchTimeDays === 1 ? "" : "s"} per your listing
                      {getSellerDispatchDeadlineFormatted(order)
                        ? ` (due by ${getSellerDispatchDeadlineFormatted(order)})`
                        : ""}
                      . Add courier and tracking below to mark it dispatched and start the buyer delivery
                      countdown.
                    </p>
                  </div>
                ) : null}
                {orderRequiresUkaraReview ? (
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#38513b]">
                        Step 1 · Required before dispatch
                      </p>
                      <h2 className="text-lg font-semibold text-slate-900">
                        UKARA verification
                      </h2>
                      <p className="text-sm text-slate-500">
                        {effectiveUkaraDecision === "verified"
                          ? "UKARA is verified. You can continue with estimated delivery and tracking below."
                          : effectiveUkaraDecision === "rejected"
                          ? "UKARA was rejected for this order. You can still use the sections below if your process allows it."
                          : "Verify or reject the buyer’s UKARA membership before you set delivery timing or add tracking."}
                      </p>
                    </div>
                    <div className="flex w-full flex-none flex-row flex-nowrap items-center justify-start gap-3 lg:w-auto lg:shrink-0 lg:justify-end">
                      <button
                        type="button"
                        className={primaryButtonClasses}
                        disabled={ukaraVerifyButtonsDisabled}
                        onClick={() => updateUkaraStatus("verified")}
                      >
                        {isUpdatingUkara ? "Updating..." : "Mark as verified"}
                      </button>
                      <button
                        type="button"
                        className={`${secondaryButtonClasses} border-rose-200 text-rose-600 hover:border-rose-400 hover:text-rose-700 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400`}
                        disabled={ukaraVerifyButtonsDisabled}
                        onClick={() => updateUkaraStatus("rejected")}
                      >
                        {isUpdatingUkara ? "Updating..." : "Reject number"}
                      </button>
                    </div>
                  </div>
                </section>
                ) : null}

                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Step {orderRequiresUkaraReview ? 2 : 1}
                      </p>
                      <h2 className="text-lg font-semibold text-slate-900">
                        Estimated Delivery {order?.estimatedDeliverySetAt ? "(from listing)" : ""}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {order?.estimatedDeliverySetAt
                          ? "Estimated delivery is calculated from your product listing’s dispatch time and delivery method. This value is locked on this order."
                          : "Set the estimated delivery time in days. The buyer will be prompted to confirm receipt after this period."}
                      </p>
                    </div>
                    <div className="w-full md:w-[360px] space-y-3">
                      {order?.estimatedDeliverySetAt ? (
                        <>
                          <div className="grid gap-1">
                            <label className="text-sm font-medium text-slate-700">
                              Estimated delivery (dispatch + delivery)
                            </label>
                            <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm">
                              {estimatedDeliveryDays ? `${estimatedDeliveryDays} days` : "—"}
                            </div>
                          </div>
                          <p className="text-xs text-slate-500">
                            Buyer has 2 additional days after this estimate to raise a dispute or confirm receipt.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="grid gap-1">
                            <label className="text-sm font-medium text-slate-700">
                              Estimated Delivery (days)
                            </label>
                            <select
                              value={estimatedDeliveryDays}
                              onChange={(e) => setEstimatedDeliveryDays(e.target.value)}
                              disabled={ukaraGateBlocksFulfillment}
                              className={`w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 ${
                                ukaraGateBlocksFulfillment
                                  ? "opacity-50 cursor-not-allowed bg-slate-50"
                                  : ""
                              }`}
                            >
                              <option value="">Select days</option>
                              {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                                <option key={day} value={day}>
                                  {day} {day === 1 ? "day" : "days"}
                                </option>
                              ))}
                            </select>
                          </div>
                          {estimatedDeliveryDays && (
                            <p className="text-xs text-slate-500">
                              Buyer will be prompted after {estimatedDeliveryDays}{" "}
                              {estimatedDeliveryDays === 1 ? "day" : "days"} from dispatch date
                            </p>
                          )}
                          {estimatedDeliveryUnchangedFromSaved ? (
                            <p className="text-xs font-medium text-emerald-700">
                              Estimated delivery saved. Continue to shipment tracking below.
                            </p>
                          ) : null}
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={handleEstimatedDeliverySave}
                              className={primaryButtonClasses}
                              disabled={
                                ukaraGateBlocksFulfillment ||
                                !estimatedDeliveryDays ||
                                estimatedDeliveryUnchangedFromSaved
                              }
                              title={
                                ukaraGateBlocksFulfillment
                                  ? "Complete UKARA verification first"
                                  : !estimatedDeliveryDays
                                  ? "Please select estimated delivery days"
                                  : estimatedDeliveryUnchangedFromSaved
                                  ? "Estimated delivery already saved"
                                  : ""
                              }
                            >
                              {estimatedDeliveryUnchangedFromSaved
                                ? "Estimated delivery saved"
                                : "Save estimated delivery"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </section>

                <section
                  id="seller-shipment-tracking"
                  className={`rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 ${
                    ukaraGateBlocksFulfillment ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Step {orderRequiresUkaraReview ? 3 : 2}
                        {orderRequiresUkaraReview ? " · After UKARA" : ""}
                      </p>
                      <h2 className="text-lg font-semibold text-slate-900">
                        Shipment tracking
                      </h2>
                      <p className="text-sm text-slate-500">
                        Add courier name, tracking code, and/or a tracking link (at least a code or link is required).
                        Paste the full URL from your courier&apos;s website. Saving marks the order as dispatched and
                        notifies the buyer.
                      </p>
                      {ukaraGateBlocksFulfillment && (
                        <p className="mt-2 text-xs font-medium text-amber-800">
                          Complete step 1 (verify or reject UKARA) before you can enter tracking or mark this order as dispatched.
                        </p>
                      )}
                    </div>
                    <div className="w-full md:w-[360px] space-y-3">
                      <div className="grid gap-3">
                        <div className="grid gap-1">
                          <label className="text-sm font-medium text-slate-700">
                            Tracking code
                          </label>
                          <input
                            type="text"
                            value={trackingCode}
                            onChange={(e) => setTrackingCode(e.target.value)}
                            disabled={ukaraGateBlocksFulfillment}
                            placeholder="e.g. 1Z999AA10123456784"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                          />
                        </div>
                        <div className="grid gap-1">
                          <label className="text-sm font-medium text-slate-700">
                            Courier
                          </label>
                          <input
                            type="text"
                            value={trackingCourier}
                            onChange={(e) => setTrackingCourier(e.target.value)}
                            disabled={ukaraGateBlocksFulfillment}
                            autoComplete="off"
                            placeholder="e.g. Royal Mail, DPD, Evri"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                          />
                        </div>
                        <div className="grid gap-1">
                          <label className="text-sm font-medium text-slate-700">
                            Tracking link
                          </label>
                          <input
                            type="text"
                            value={trackingLink}
                            onChange={(e) => setTrackingLink(e.target.value)}
                            disabled={ukaraGateBlocksFulfillment}
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="Paste full tracking URL from courier site"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 disabled:cursor-not-allowed disabled:bg-slate-50"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={handleTrackingSave}
                          className={primaryButtonClasses}
                          disabled={ukaraGateBlocksFulfillment || trackingUnchangedFromSaved}
                          title={
                            ukaraGateBlocksFulfillment
                              ? "Complete UKARA verification first"
                              : trackingUnchangedFromSaved
                                ? "Change tracking details to save again"
                                : ""
                          }
                        >
                          Save & mark dispatched
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {isOrderCompleted(order) && (
              <section
                id="order-reviews"
                className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
              >
                {!reviewExchange?.youSubmitted && (
                  <div className="mb-4 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3">
                    <p className="text-sm font-semibold text-amber-900">
                      Veteran Airsoft — leave a review
                    </p>
                    <p className="mt-1 text-sm text-amber-800">
                      Rate this buyer. Their review of you stays hidden until you both submit
                      yours.
                    </p>
                  </div>
                )}
                {reviewExchange?.youSubmitted && !reviewExchange?.mutualComplete && (
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-sm text-slate-700">
                      Thanks — your review is saved. You will see the buyer&apos;s review once
                      they submit theirs.
                    </p>
                  </div>
                )}
                <h2 className="text-lg font-semibold text-slate-900">Rate this buyer</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Your rating helps other sellers. The buyer&apos;s review of you appears only after
                  you both submit.
                </p>
                {!existingCustomerReview ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setIsRatingModalOpen(true)}
                      className={primaryButtonClasses}
                    >
                      Rate buyer
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                    <p className="text-sm font-medium text-slate-800">Your buyer rating</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Ratings rating={existingCustomerReview.rating || 0} />
                      <span className="text-sm text-slate-600">
                        {existingCustomerReview.rating} out of 5
                      </span>
                    </div>
                    {existingCustomerReview.comment ? (
                      <p className="mt-2 text-sm text-slate-700">{existingCustomerReview.comment}</p>
                    ) : null}
                  </div>
                )}
                {reviewExchange?.mutualComplete && reviewExchange?.otherReview && (
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <p className="text-sm font-medium text-slate-800">Buyer&apos;s review of you</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Ratings rating={reviewExchange.otherReview.rating || 0} />
                      <span className="text-sm text-slate-600">
                        {reviewExchange.otherReview.rating} out of 5
                      </span>
                    </div>
                    {reviewExchange.otherReview.comment ? (
                      <p className="mt-2 text-sm text-slate-700">
                        {reviewExchange.otherReview.comment}
                      </p>
                    ) : null}
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </div>

      {isRatingModalOpen && (
        <MobileFriendlyModal
          onClose={() => setIsRatingModalOpen(false)}
          panelClassName="max-w-md"
          zClass="z-50"
          backdropClassName="bg-black/40"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Rate Customer</h3>
            <button
              type="button"
              onClick={() => setIsRatingModalOpen(false)}
              className="p-2 hover:bg-slate-100 rounded-lg transition"
            >
              <RxCross1 size={20} />
            </button>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            How would you rate this buyer? Their review of you stays hidden until you both
            submit yours.
          </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Rating (1-5 stars)
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setCustomerRating(star)}
                    className="focus:outline-none"
                  >
                    {star <= customerRating ? (
                      <AiFillStar size={32} color="#f6b100" className="cursor-pointer" />
                    ) : (
                      <AiOutlineStar size={32} color="#d1d5db" className="cursor-pointer" />
                    )}
                  </button>
                ))}
              </div>
              {customerRating > 0 && (
                <p className="mt-2 text-sm text-slate-600">
                  {customerRating === 5 && "Excellent"}
                  {customerRating === 4 && "Good"}
                  {customerRating === 3 && "Average"}
                  {customerRating === 2 && "Below Average"}
                  {customerRating === 1 && "Poor"}
                </p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Comment (Optional)
              </label>
              <textarea
                value={customerRatingComment}
                onChange={(e) => setCustomerRatingComment(e.target.value)}
                placeholder="Share your experience with this customer..."
                rows={4}
                className={MODAL_TEXTAREA_CLASS}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsRatingModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition"
                disabled={isSubmittingRating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomerRatingSubmit}
                className="px-4 py-2 rounded-lg bg-[#38513b] text-white font-medium hover:bg-[#2f4232] transition disabled:opacity-50"
                disabled={isSubmittingRating || customerRating === 0}
              >
                {isSubmittingRating ? "Submitting..." : existingCustomerReview ? "Update Rating" : "Submit Rating"}
              </button>
            </div>
        </MobileFriendlyModal>
      )}
    </div>
  );
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

export default OrderDetails;
